import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, 
  AreaChart, Area, Legend
} from 'recharts';
import { 
  ArrowLeft, TrendingUp, TrendingDown, Minus, RefreshCw, 
  Lightbulb, AlertTriangle, CheckCircle, Activity, Calendar,
  ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import { formatCurrency } from '@/lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Color palette for regions
const REGION_COLORS = {
  PJM: '#3B82F6',
  ERCOT: '#EF4444',
  CAISO: '#F59E0B',
  NYISO: '#8B5CF6',
  ISONE: '#10B981',
  MISO: '#EC4899',
  SPP: '#06B6D4'
};

// Mini sparkline component
function Sparkline({ data, color, height = 30, trend }) {
  if (!data || data.length === 0) return null;
  
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  
  // Normalize data to 0-100 range
  const normalizedData = data.map((value, index) => ({
    index,
    value: ((value - min) / range) * 100
  }));
  
  return (
    <div className="flex items-center gap-2">
      <ResponsiveContainer width={80} height={height}>
        <AreaChart data={normalizedData} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
          <defs>
            <linearGradient id={`gradient-${color}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={color} stopOpacity={0.3}/>
              <stop offset="95%" stopColor={color} stopOpacity={0}/>
            </linearGradient>
          </defs>
          <Area 
            type="monotone" 
            dataKey="value" 
            stroke={color} 
            fill={`url(#gradient-${color})`}
            strokeWidth={1.5}
            dot={false}
          />
        </AreaChart>
      </ResponsiveContainer>
      {trend === 'up' && <ArrowUpRight className="w-4 h-4 text-green-500" />}
      {trend === 'down' && <ArrowDownRight className="w-4 h-4 text-red-500" />}
      {trend === 'stable' && <Minus className="w-4 h-4 text-gray-400" />}
    </div>
  );
}

// Trend indicator component
function TrendIndicator({ direction, change, metric, inverse = false }) {
  const isPositive = inverse 
    ? (direction === 'down' || change < 0)
    : (direction === 'up' || change > 0);
  const isNegative = inverse 
    ? (direction === 'up' || change > 0)
    : (direction === 'down' || change < 0);
  
  return (
    <div className="flex items-center gap-1">
      {direction === 'up' && (
        <TrendingUp className={`w-4 h-4 ${isPositive ? 'text-green-500' : 'text-red-500'}`} />
      )}
      {direction === 'down' && (
        <TrendingDown className={`w-4 h-4 ${isNegative ? 'text-red-500' : 'text-green-500'}`} />
      )}
      {direction === 'stable' && (
        <Minus className="w-4 h-4 text-gray-400" />
      )}
      <span className={`text-xs font-medium ${
        isPositive ? 'text-green-600' : 
        isNegative ? 'text-red-600' : 'text-gray-500'
      }`}>
        {change > 0 ? '+' : ''}{change}{metric === 'value' ? '%' : ''}
      </span>
    </div>
  );
}

export default function TerritoryTrends() {
  const navigate = useNavigate();
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [months, setMonths] = useState('6');
  const [trendsData, setTrendsData] = useState(null);

  // Fetch territories
  const { data: territoriesData } = useQuery({
    queryKey: ['territories'],
    queryFn: async () => {
      const token = localStorage.getItem('ccip_token');
      const response = await axios.get(`${BACKEND_URL}/api/territories`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
  });

  // Trends mutation
  const trendsMutation = useMutation({
    mutationFn: async (params) => {
      const token = localStorage.getItem('ccip_token');
      const response = await axios.post(
        `${BACKEND_URL}/api/territories/trends`,
        params,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    },
    onSuccess: (data) => {
      setTrendsData(data);
    },
  });

  const toggleRegion = (region) => {
    setSelectedRegions(prev => {
      if (prev.includes(region)) {
        return prev.filter(r => r !== region);
      }
      if (prev.length >= 7) {
        return prev;
      }
      return [...prev, region];
    });
  };

  const selectAllRegions = () => {
    const allRegions = territories.map(t => t.iso_region);
    setSelectedRegions(allRegions);
  };

  const analyzeТrends = () => {
    if (selectedRegions.length >= 1) {
      trendsMutation.mutate({
        regions: selectedRegions,
        months: parseInt(months)
      });
    }
  };

  const territories = territoriesData?.territories || [];
  const trends = trendsData?.trends ? Object.values(trendsData.trends) : [];

  // Prepare combined chart data
  const combinedChartData = trends.length > 0 
    ? trends[0].monthlyData.map((_, index) => {
        const point = { month: trends[0].monthlyData[index].month };
        trends.forEach(region => {
          point[`${region.region}_competitiveness`] = region.monthlyData[index]?.avgCompetitiveness || 0;
          point[`${region.region}_atRisk`] = region.monthlyData[index]?.atRiskPercent || 0;
          point[`${region.region}_renewal`] = region.monthlyData[index]?.renewalRate || 0;
        });
        return point;
      })
    : [];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="territory-trends">
      {/* Header */}
      <div className="bg-white border-b p-4">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/map/compare')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Comparison
            </Button>
            <div>
              <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <span className="text-gradient-primary">Territory Trend Analysis</span>
              </h1>
              <p className="text-sm text-muted-foreground">
                Track how regional metrics have evolved over time
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        {/* Region & Time Selection */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Activity className="w-5 h-5 text-[#09409C]" />
              Select Regions & Time Period
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Time Period Selection */}
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm">Time Period:</span>
                </div>
                <Select value={months} onValueChange={setMonths}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="3">3 Months</SelectItem>
                    <SelectItem value="6">6 Months</SelectItem>
                    <SelectItem value="9">9 Months</SelectItem>
                    <SelectItem value="12">12 Months</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Region Selection */}
              <div className="grid grid-cols-7 gap-3">
                {territories.map((territory) => (
                  <div
                    key={territory.iso_region}
                    className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      selectedRegions.includes(territory.iso_region)
                        ? 'border-[#09409C] bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => toggleRegion(territory.iso_region)}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Checkbox 
                        checked={selectedRegions.includes(territory.iso_region)}
                        onCheckedChange={() => toggleRegion(territory.iso_region)}
                      />
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: REGION_COLORS[territory.iso_region] }}
                      />
                      <span className="font-semibold text-sm">{territory.iso_region}</span>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Actions */}
              <div className="flex items-center justify-between pt-2">
                <div className="flex items-center gap-2">
                  <Badge variant="outline">{selectedRegions.length} selected</Badge>
                  <Button variant="ghost" size="sm" onClick={selectAllRegions}>
                    Select All
                  </Button>
                </div>
                <Button 
                  onClick={analyzeТrends}
                  disabled={selectedRegions.length < 1 || trendsMutation.isPending}
                  className="bg-[#09409C] hover:bg-[#146CBB]"
                >
                  {trendsMutation.isPending ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <Activity className="w-4 h-4 mr-2" />
                      Analyze Trends
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        {trendsData && (
          <div className="space-y-6">
            {/* Summary */}
            <div className="grid grid-cols-4 gap-4">
              <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-5 h-5 text-green-600" />
                    <span className="text-sm text-green-700">Improving</span>
                  </div>
                  <div className="text-2xl font-bold text-green-800">
                    {trendsData.summary.improvingRegions.length}
                  </div>
                  <div className="text-xs text-green-600 mt-1">
                    {trendsData.summary.improvingRegions.join(', ') || 'None'}
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingDown className="w-5 h-5 text-red-600" />
                    <span className="text-sm text-red-700">Declining</span>
                  </div>
                  <div className="text-2xl font-bold text-red-800">
                    {trendsData.summary.decliningRegions.length}
                  </div>
                  <div className="text-xs text-red-600 mt-1">
                    {trendsData.summary.decliningRegions.join(', ') || 'None'}
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Minus className="w-5 h-5 text-gray-600" />
                    <span className="text-sm text-gray-700">Stable</span>
                  </div>
                  <div className="text-2xl font-bold text-gray-800">
                    {trendsData.summary.stableRegions.length}
                  </div>
                  <div className="text-xs text-gray-600 mt-1">
                    {trendsData.summary.stableRegions.join(', ') || 'None'}
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="w-5 h-5 text-blue-600" />
                    <span className="text-sm text-blue-700">Period</span>
                  </div>
                  <div className="text-2xl font-bold text-blue-800">
                    {trendsData.summary.monthsAnalyzed}
                  </div>
                  <div className="text-xs text-blue-600 mt-1">Months Analyzed</div>
                </CardContent>
              </Card>
            </div>

            {/* Insights */}
            {trendsData.insights && trendsData.insights.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Lightbulb className="w-5 h-5 text-yellow-500" />
                    Key Insights
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-3">
                    {trendsData.insights.map((insight, idx) => (
                      <div 
                        key={idx}
                        className={`p-3 rounded-lg border ${
                          insight.type === 'positive' 
                            ? 'bg-green-50 border-green-200' 
                            : 'bg-orange-50 border-orange-200'
                        }`}
                      >
                        <div className="flex items-start gap-2">
                          {insight.type === 'positive' ? (
                            <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                          ) : (
                            <AlertTriangle className="w-4 h-4 text-orange-600 mt-0.5" />
                          )}
                          <div>
                            <Badge 
                              variant="outline" 
                              className="text-[10px] mb-1"
                              style={{ borderColor: REGION_COLORS[insight.region] }}
                            >
                              {insight.region}
                            </Badge>
                            <p className="text-sm text-gray-700">{insight.message}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Regional Trend Cards with Sparklines */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Regional Metrics Trends</CardTitle>
                <CardDescription>Sparklines show {months}-month trend for each metric</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-2 text-sm font-medium">Region</th>
                        <th className="text-left py-3 px-2 text-sm font-medium">Competitiveness</th>
                        <th className="text-left py-3 px-2 text-sm font-medium">At-Risk %</th>
                        <th className="text-left py-3 px-2 text-sm font-medium">Healthy %</th>
                        <th className="text-left py-3 px-2 text-sm font-medium">Renewal Rate</th>
                        <th className="text-left py-3 px-2 text-sm font-medium">Portfolio Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trends.map((region) => (
                        <tr key={region.region} className="border-b hover:bg-gray-50">
                          <td className="py-4 px-2">
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-3 h-3 rounded-full" 
                                style={{ backgroundColor: REGION_COLORS[region.region] }}
                              />
                              <span className="font-medium">{region.region}</span>
                            </div>
                          </td>
                          <td className="py-4 px-2">
                            <div className="flex flex-col gap-1">
                              <Sparkline 
                                data={region.sparklineData.competitiveness} 
                                color={REGION_COLORS[region.region]}
                                trend={region.trendDirections.competitiveness}
                              />
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-medium">{region.currentStats.avgCompetitiveness}%</span>
                                <TrendIndicator 
                                  direction={region.trendDirections.competitiveness}
                                  change={region.changes.competitiveness}
                                />
                              </div>
                            </div>
                          </td>
                          <td className="py-4 px-2">
                            <div className="flex flex-col gap-1">
                              <Sparkline 
                                data={region.sparklineData.atRisk} 
                                color="#EF4444"
                                trend={region.trendDirections.atRisk}
                              />
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-medium">{region.currentStats.atRiskPercent}%</span>
                                <TrendIndicator 
                                  direction={region.trendDirections.atRisk}
                                  change={region.changes.atRisk}
                                  inverse={true}
                                />
                              </div>
                            </div>
                          </td>
                          <td className="py-4 px-2">
                            <div className="flex flex-col gap-1">
                              <Sparkline 
                                data={region.sparklineData.healthy} 
                                color="#22C55E"
                                trend={region.trendDirections.healthy}
                              />
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-medium">{region.currentStats.healthyPercent}%</span>
                                <TrendIndicator 
                                  direction={region.trendDirections.healthy}
                                  change={region.changes.healthy}
                                />
                              </div>
                            </div>
                          </td>
                          <td className="py-4 px-2">
                            <div className="flex flex-col gap-1">
                              <Sparkline 
                                data={region.sparklineData.renewalRate} 
                                color="#8B5CF6"
                                trend={region.trendDirections.renewalRate}
                              />
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-medium">{region.currentStats.renewalRate}%</span>
                                <TrendIndicator 
                                  direction={region.trendDirections.renewalRate}
                                  change={region.changes.renewalRate}
                                />
                              </div>
                            </div>
                          </td>
                          <td className="py-4 px-2">
                            <div className="flex flex-col gap-1">
                              <Sparkline 
                                data={region.sparklineData.value} 
                                color="#09409C"
                                trend={region.trendDirections.value}
                              />
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-medium">{formatCurrency(region.currentStats.totalValue)}</span>
                                <TrendIndicator 
                                  direction={region.trendDirections.value}
                                  change={region.changes.value}
                                  metric="value"
                                />
                              </div>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Combined Line Charts */}
            <div className="grid grid-cols-2 gap-6">
              {/* Competitiveness Over Time */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Competitiveness Trend</CardTitle>
                  <CardDescription>Average competitiveness score over time</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={combinedChartData}>
                      <XAxis dataKey="month" />
                      <YAxis domain={[0, 100]} />
                      <Tooltip />
                      <Legend />
                      {trends.map((region) => (
                        <Line
                          key={region.region}
                          type="monotone"
                          dataKey={`${region.region}_competitiveness`}
                          name={region.region}
                          stroke={REGION_COLORS[region.region]}
                          strokeWidth={2}
                          dot={{ r: 4 }}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* At-Risk Trend */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">At-Risk Trend</CardTitle>
                  <CardDescription>Percentage of at-risk contracts over time</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={combinedChartData}>
                      <XAxis dataKey="month" />
                      <YAxis domain={[0, 100]} />
                      <Tooltip />
                      <Legend />
                      {trends.map((region) => (
                        <Line
                          key={region.region}
                          type="monotone"
                          dataKey={`${region.region}_atRisk`}
                          name={region.region}
                          stroke={REGION_COLORS[region.region]}
                          strokeWidth={2}
                          dot={{ r: 4 }}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Renewal Rate Trend */}
              <Card className="col-span-2">
                <CardHeader>
                  <CardTitle className="text-lg">Renewal Rate Trend</CardTitle>
                  <CardDescription>Renewal success rate over time</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={combinedChartData}>
                      <XAxis dataKey="month" />
                      <YAxis domain={[0, 100]} />
                      <Tooltip />
                      <Legend />
                      {trends.map((region) => (
                        <Line
                          key={region.region}
                          type="monotone"
                          dataKey={`${region.region}_renewal`}
                          name={region.region}
                          stroke={REGION_COLORS[region.region]}
                          strokeWidth={2}
                          dot={{ r: 4 }}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!trendsData && !trendsMutation.isPending && (
          <Card className="py-12">
            <CardContent className="text-center">
              <Activity className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-semibold mb-2">Analyze Regional Trends</h3>
              <p className="text-muted-foreground">
                Select regions and a time period to see how metrics have evolved
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
